import React, { useEffect, useState, useCallback } from "react";
import { useParams } from "react-router-dom";
import { useNetworkIdRedirect } from "./common/hooks";
import { Container, Row, Col, Button, Form } from "react-bootstrap";
import ContentWrapper from "components/content-wrapper/ContentWrapper";
import refreshIcon from "assets/svg/refresh-green.svg";
import { IconButton } from "@mui/material";
import { DownloadDataUI } from "components/gateways/DownloadDataUI";
import moment from "moment";
import { getCalendarFormat } from "utils/datetime";
import styledConst from "styles";
import { HttpStatus, ACTUATOR_NAME_MESSAGE, ACTUATOR_NAME_SUCCESS_MESSAGE, Patterns, TRY_AGAIN_ERROR_MESSAGE } from "constant";
import {
    getOneRegistry,
    getRegistryConfig,
    getGwAndLDSUStatus,
    getSensorLastReading,
    updateActuatorCommand,
} from "service/gatewayService";
import { getLDSBusInfo } from "utils/gatewayFunctions";
import { isHttpSuccess } from "utils/functions";
import { getAPIError, showErrorAlert, showSuccessAlert } from "utils/alert";
import AxiosInstance from "service/axiosInstance";
import { useOrganisation } from "hooks/organization";
import {
    BREADCRUMB_LABELS,
    DEFAULT_VALUES,
    STYLE_CONSTANTS,
    BUS_COUNT,
} from "./constants";
import { getTagDeviceUID, getTagDeviceSAID } from "./common/deviceUtils";
import { DynamicBreadcrumb } from "./common/DynamicBreadcrumb";
import { getControllerStatusFromReading2 } from "./common/controllerReadingUtils";

// Types
type ControllerReading = {
    value: number | null;
    status: number | null;
    timestamp: number;
};

type Controller = {
    UID: string;
    name: string;
    type?: string;
    controller_type?: string;
    ldsuName?: string;
    ldsuUuid?: string;
    hubPort?: string;
    roomMembership?: string;
    groupsMembership?: string[];
    readings?: ControllerReading;
    said?: number;
};

const tagHasControllerUID = (
    tag: any,
    controllerUID: string,
    controllerSaid?: number | null
): boolean => {
    if (!tag.devices || !Array.isArray(tag.devices)) {
        return false;
    }

    return tag.devices.some((device: any) => {
        const uid = getTagDeviceUID(device);
        if (!uid || String(uid) !== String(controllerUID)) {
            return false;
        }
        if (controllerSaid == null) {
            return true;
        }
        const said = getTagDeviceSAID(device);
        return said === controllerSaid;
    });
};

const updateMembershipFromTag = (
    tagValue: string,
    rooms: string[],
    zones: string[],
    groups: string[]
): void => {
    // Ignore hub tags since hub name is not displayed for controllers
    if (tagValue.startsWith("hub/")) {
        return;
    }

    if (tagValue.startsWith("zone/")) {
        const zoneName = tagValue.replace("zone/", "");
        if (!zones.includes(zoneName)) {
            zones.push(zoneName);
        }
        return;
    }

    if (tagValue.startsWith("room/")) {
        const roomName = tagValue.replace("room/", "");
        if (!rooms.includes(roomName)) {
            rooms.push(roomName);
        }
        return;
    }

    if (tagValue.startsWith("group/")) {
        const groupName = tagValue.replace("group/", "");
        if (!groups.includes(groupName)) {
            groups.push(groupName);
        }
    }
};

const ControllerDetails = (): JSX.Element => {
    const { id, controllerId } = useParams<{ id: string; controllerId: string }>();
    const [controller, setController] = useState<Controller | null>(null);
    const [controllerName, setControllerName] = useState("");
    const [isControllerNameEditing, setIsControllerNameEditing] = useState(false);
    const [isLoading, setIsLoading] = useState(true);
    const [error, setError] = useState<string | null>(null);
    const [roomMembership, setRoomMembership] = useState<string[]>([]);
    const [zoneMembership, setZoneMembership] = useState<string[]>([]);
    const [groupMembership, setGroupMembership] = useState<string[]>([]);
    const [networkName, setNetworkName] = useState<string>(DEFAULT_VALUES.NETWORK_NAME);
    const [isRefreshing, setIsRefreshing] = useState(false);
    const canEditControllerName = false;

    const { currentOrgId } = useOrganisation();

    /**
     * Fetches hub name, room membership, and group membership using the tags API
     * Tags API provides membership information by checking which tags contain the controller UID
     * @param controllerUID - The controller device UID
     * @param gatewayId - The gateway ID (used for fetching network name from registry)
     */
    const fetchControllerMembership = useCallback(
        async (controllerUID: string, gatewayId: string, controllerSaid?: number | null) => {
            if (!currentOrgId || !controllerUID) {
                return;
            }

            try {
                // Fetch all tags with relationship using tags API
                const tagsResponse = await AxiosInstance.get("/tags", {
                    params: {
                        organisation_id: currentOrgId,
                        with_relationship: true,
                    },
                });

                if (!isHttpSuccess(tagsResponse.status)) {
                    return;
                }

                const tags = tagsResponse.data?.data || [];
                const rooms: string[] = [];
                const zones: string[] = [];
                const groups: string[] = [];

                // Find tags that contain this controller UID
                // Tag value format: "hub/name", "zone/name", "room/name", "group/name"
                for (const tag of tags) {
                    if (!tagHasControllerUID(tag, controllerUID, controllerSaid)) {
                        continue;
                    }

                    const tagValue = tag.value || "";
                    updateMembershipFromTag(tagValue, rooms, zones, groups);
                }

                // Set zone, room and group membership from tags API
                setZoneMembership(zones);
                setRoomMembership(rooms);
                setGroupMembership(groups);

                // Fetch network name from registry
                try {
                    const registryRes = await getOneRegistry(gatewayId);
                    if (isHttpSuccess(registryRes.status)) {
                        const fetchedNetworkName = registryRes.data?.name || DEFAULT_VALUES.NETWORK_NAME;
                        setNetworkName(fetchedNetworkName);
                    }
                } catch (error) {
                    console.error("Error fetching network name:", error);
                }
            } catch (error) {
                console.error("Error fetching controller membership:", error);
            }
        },
        [currentOrgId]
    );

    // Fetch controller data from API
    const fetchControllerData = useCallback(async () => {
        setIsLoading(true);
        setError(null);
        
        try {
            const ldsuGatewayId = id;
            
            // Fetch LDSU data to get controller information
            const [registryInfoRes, registryConfigRes, registryGwAndLdsuStatusRes] = await Promise.all([
                getOneRegistry(ldsuGatewayId),
                getRegistryConfig(ldsuGatewayId),
                getGwAndLDSUStatus(ldsuGatewayId)
            ]);

            if ([registryInfoRes.status, registryConfigRes.status, registryGwAndLdsuStatusRes.status].includes(HttpStatus.FORBIDDEN)) {
                setError("Access forbidden. Please check your permissions.");
                return;
            }

            if (!isHttpSuccess(registryInfoRes.status) || !isHttpSuccess(registryConfigRes.status) || !isHttpSuccess(registryGwAndLdsuStatusRes.status)) {
                setError("Failed to fetch controller data. Please try again.");
                return;
            }

            // Get actuator data from all buses
            const allActuators = [];
            for (let busNumber = 1; busNumber <= BUS_COUNT; busNumber++) {
                const { actuators } = getLDSBusInfo(
                    busNumber.toString(),
                    registryInfoRes.data?.info?.devices || [],
                    registryConfigRes.data || [],
                    registryGwAndLdsuStatusRes.data?.devices || []
                );

                allActuators.push(...actuators);
            }

            // Find the specific controller by name or ID
            const foundController = allActuators.find(actuator => 
                actuator.name === controllerId || 
                actuator.UID === controllerId ||
                `${actuator.UID}-${actuator.said}` === controllerId
            );

            if (!foundController) {
                setError("Controller not found");
                return;
            }

            // Derive LDSU / auxiliary hub identity for this controller
            const ldsuName =
                (foundController as any).ldsu_name ||
                DEFAULT_VALUES.HUB_NAME;

            const controllerWithDefaults = {
                ...foundController,
                ldsuName,
                hubName: DEFAULT_VALUES.HUB_NAME, 
                hubPort: DEFAULT_VALUES.HUB_PORT, 
                roomMembership: DEFAULT_VALUES.ROOM_MEMBERSHIP, 
                groupsMembership: [] 
            };

            // Fetch per-device reading2 for this controller (no full bus scan).
            const controllerReadings = await getSensorLastReading(
                ldsuGatewayId,
                foundController.UID
            );

            if (isHttpSuccess(controllerReadings.status)) {
                const parsed = getControllerStatusFromReading2(
                    controllerReadings.data,
                    foundController.said
                );
                if (parsed) {
                    const controllerWithReadings = {
                        ...controllerWithDefaults,
                        readings: {
                            value: parsed.status,
                            status: parsed.status,
                            timestamp: parsed.timestamp,
                        },
                    };
                    setController(controllerWithReadings);
                } else {
                    setController(controllerWithDefaults);
                }
            } else {
                setController(controllerWithDefaults);
            }

            setControllerName(foundController.name || controllerId);

            // Store stable identifiers for later status refreshes
            setControllerUID(foundController.UID);
            setControllerSaid(foundController.said);

            // Fetch controller membership information (hub, room, group, network) using tags API
            // This will also fetch the network name
            if (foundController.UID && ldsuGatewayId) {
                fetchControllerMembership(foundController.UID, ldsuGatewayId, foundController.said);
            }
            
        } catch (error) {
            console.error("Error fetching controller data:", error);
            setError("Failed to fetch controller data. Please try again.");
        } finally {
            setIsLoading(false);
        }
    }, [id, controllerId, fetchControllerMembership]);

    // Stable identifiers for controller status refresh
    const [controllerUID, setControllerUID] = useState<string | null>(null);
    const [controllerSaid, setControllerSaid] = useState<number | null>(null);

    // Dedicated function to update only the status/readings of the controller
    const updateControllerStatus = useCallback(async () => {
        if (!id || !controllerUID || controllerSaid == null) {
            return;
        }

        setIsRefreshing(true);
        try {
            const controllerReadings = await getSensorLastReading(id, controllerUID);
            if (isHttpSuccess(controllerReadings.status)) {
                const parsed = getControllerStatusFromReading2(
                    controllerReadings.data,
                    controllerSaid
                );
                if (parsed) {
                    const updatedReadings: ControllerReading = {
                        value: parsed.status,
                        status: parsed.status,
                        timestamp: parsed.timestamp,
                    };

                    setController(prev => {
                        if (!prev) return prev;
                        return {
                            ...prev,
                            readings: updatedReadings,
                        };
                    });
                }
            }
        } catch (error) {
            console.error("Error updating controller status:", error);
        } finally {
            setIsRefreshing(false);
        }
    }, [id, controllerUID, controllerSaid]);

    const { validateAndRedirect } = useNetworkIdRedirect(id, false);
    
    useEffect(() => {
        if (!validateAndRedirect()) {
            return;
        }
        if (controllerId) {
            fetchControllerData();
        }
    }, [controllerId, id, validateAndRedirect, fetchControllerData]);

    const toggleControllerNameEdit = () => {
        if (!canEditControllerName) {
            return;
        }
        setIsControllerNameEditing(!isControllerNameEditing);
    };

    const saveControllerName = async () => {
        if (!canEditControllerName) {
            return;
        }
        if (!controller) {
            return;
        }

        const nameTrim = controllerName.trim();

        if (controller.name === nameTrim) {
            setIsControllerNameEditing(false);
            setControllerName(controller.name);
            return;
        }

        if (!Patterns.gatewayNamePattern.test(nameTrim)) {
            showErrorAlert({
                message: ACTUATOR_NAME_MESSAGE,
            });
            return;
        }

        try {
            const saidString = String(controller.said ?? "");
            const updateNameRes = await updateActuatorCommand(
                id,
                controller.UID,
                saidString,
                { name: nameTrim }
            );

            if (isHttpSuccess(updateNameRes.status)) {
                showSuccessAlert({
                    message: ACTUATOR_NAME_SUCCESS_MESSAGE,
                });
                setController({ ...controller, name: nameTrim });
                setControllerName(nameTrim);
                setIsControllerNameEditing(false);
            } else {
                showErrorAlert(
                    getAPIError(updateNameRes, TRY_AGAIN_ERROR_MESSAGE)
                );
            }
        } catch (error) {
            console.error("Error updating controller name:", error);
            showErrorAlert({
                message: TRY_AGAIN_ERROR_MESSAGE,
            });
        }
    };

    const renderDateTime = (readings: ControllerReading | undefined): string => {
        const timestamp = readings?.timestamp;
        if (!timestamp) {
            return DEFAULT_VALUES.NEVER;
        }
        return moment(timestamp, "X").calendar(getCalendarFormat());
    };

    if (isLoading) {
        return (
            <ContentWrapper>
                <div className="page-content">
                    <Container fluid>
                        <Row>
                            <Col sm={12}>
                                <h5 className="page-title">Controller Details</h5>
                                <DynamicBreadcrumb currentPageLabel={BREADCRUMB_LABELS["controller-details"]} />
                            </Col>
                        </Row>
                        <Row>
                            <Col sm={8}>
                                <div 
                                    className="controller-loading-panel" 
                                    style={{
                                        backgroundColor: STYLE_CONSTANTS.PANEL_BACKGROUND,
                                        borderRadius: STYLE_CONSTANTS.PANEL_BORDER_RADIUS,
                                        padding: STYLE_CONSTANTS.PANEL_PADDING,
                                        marginBottom: STYLE_CONSTANTS.PANEL_MARGIN_BOTTOM,
                                        textAlign: 'center'
                                    }}
                                >
                                    <p>Loading controller data...</p>
                                </div>
                            </Col>
                        </Row>
                    </Container>
                </div>
            </ContentWrapper>
        );
    }

    if (error || !controller) {
        return (
            <ContentWrapper>
                <div className="page-content">
                    <Container fluid>
                        <Row>
                            <Col sm={12}>
                                <h5 className="page-title">Controller Details</h5>
                                <DynamicBreadcrumb currentPageLabel={BREADCRUMB_LABELS["controller-details"]} />
                            </Col>
                        </Row>
                        <Row>
                            <Col sm={8}>
                                <div 
                                    className="controller-error-panel" 
                                    style={{
                                        backgroundColor: STYLE_CONSTANTS.PANEL_BACKGROUND,
                                        borderRadius: STYLE_CONSTANTS.PANEL_BORDER_RADIUS,
                                        padding: STYLE_CONSTANTS.PANEL_PADDING,
                                        marginBottom: STYLE_CONSTANTS.PANEL_MARGIN_BOTTOM
                                    }}
                                >
                                    <p style={{ color: STYLE_CONSTANTS.ERROR_COLOR }}>
                                        {error || "Controller not found or loading..."}
                                    </p>
                                    <Button 
                                        variant="outline-light" 
                                        onClick={fetchControllerData}
                                        style={{ marginTop: '10px' }}
                                    >
                                        Retry
                                    </Button>
                                </div>
                            </Col>
                        </Row>
                    </Container>
                </div>
            </ContentWrapper>
        );
    }

    let zoneOrRoomMembershipValue: string = DEFAULT_VALUES.ROOM_MEMBERSHIP;
    if (zoneMembership.length > 0) {
        zoneOrRoomMembershipValue = zoneMembership.join(", ");
    } else if (roomMembership.length > 0) {
        zoneOrRoomMembershipValue = roomMembership.join(", ");
    }

    return (
        <ContentWrapper>
            <div className="page-content">
                <Container fluid>
                    <Row>
                        <Col sm={12}>
                            <h5 className="page-title">{controller.name}</h5>
                            <DynamicBreadcrumb currentPageLabel={BREADCRUMB_LABELS["controller-details"]} />
                        </Col>
                    </Row>

                    <Row className="flex-row">
                        <Col lg={6} xs={12}>
                            <div className="room-summary rounded shadow-sm">
                                {/* Controller Status Section */}
                                <div 
                                    className="form-box mb-2" 
                                    style={{ 
                                        paddingLeft: STYLE_CONSTANTS.FORM_BOX_PADDING, 
                                        paddingRight: STYLE_CONSTANTS.FORM_BOX_PADDING, 
                                        paddingTop: STYLE_CONSTANTS.FORM_BOX_PADDING_TOP_BOTTOM, 
                                        paddingBottom: STYLE_CONSTANTS.FORM_BOX_PADDING_TOP_BOTTOM 
                                    }}
                                >
                                    <div>
                                        <div className="descriptor-item d-flex" style={{ borderBottom: "none" }}>
                                            <h5 className="flex-grow-1">Status</h5>
                                            <div style={{ textAlign: "right" }}>
                                                <span
                                                    style={{
                                                        color:
                                                            controller.readings?.status === 1
                                                                ? styledConst.Secondary_Green
                                                                : styledConst.Secondary_Red,
                                                        marginRight: 8,
                                                    }}
                                                >
                                                    &#9679;
                                                </span>
                                                {controller.readings?.status === 1 ? (
                                                    <>
                                                        On{" "}
                                                        <span style={{ color: STYLE_CONSTANTS.TEXT_COLOR_SECONDARY }}>
                                                            {renderDateTime(controller.readings)}
                                                        </span>
                                                    </>
                                                ) : (
                                                    <>
                                                        Off{" "}
                                                        <span style={{ color: STYLE_CONSTANTS.TEXT_COLOR_SECONDARY }}>
                                                            {renderDateTime(controller.readings)}
                                                        </span>
                                                    </>
                                                )}
                                                <IconButton
                                                    disableRipple
                                                    className={
                                                        isRefreshing
                                                            ? "mr-2 bigRefresh refresh-animation"
                                                            : "mr-2 bigRefresh"
                                                    }
                                                    style={{ background: 'none', border: 'none', padding: 0, cursor: 'pointer' }}
                                                    aria-label="Refresh controller status"
                                                    onClick={updateControllerStatus}
                                                    onKeyDown={updateControllerStatus}
                                                >
                                                    <img
                                                        src={refreshIcon}
                                                        alt="refresh"
                                                    />
                                                </IconButton>
                                            </div>
                                        </div>
                                    </div>
                                </div>

                                {/* Controller Name Section */}
                                <div className="form-box mt-2">
                                    <Form.Row>
                                        <Col md="12">
                                            <Form.Label>Actuator Name</Form.Label>
                                        </Col>
                                        <Col lg={8} xs={12} className="mb-2">
                                            <Form.Group className="mb-0">
                                                <Form.Control
                                                    type="text"
                                                    required
                                                    aria-label="name"
                                                    disabled={!isControllerNameEditing}
                                                    value={controllerName}
                                                    onChange={(e) => {
                                                        setControllerName(e.target.value);
                                                    }}
                                                    onKeyPress={(e: React.KeyboardEvent<HTMLInputElement>) => {
                                                        if (e.key === "Enter") {
                                                            e.preventDefault();
                                                            saveControllerName();
                                                        }
                                                    }}
                                                />
                                            </Form.Group>
                                        </Col>
                                        <Col lg={4} xs={12}>
                                            {isControllerNameEditing ? (
                                                <Button
                                                    type="submit"
                                                    className="w-100"
                                                    onClick={saveControllerName}
                                                >
                                                    Save
                                                </Button>
                                            ) : (
                                                <Button
                                                    type="button"
                                                    className="w-100"
                                                    onClick={toggleControllerNameEdit}
                                                    disabled={!canEditControllerName}
                                                >
                                                    EDIT
                                                </Button>
                                            )}
                                        </Col>
                                    </Form.Row>
                                </div>

                                {/* Controller Details Section */}
                                <div className="form-box mt-2 pb-5">
                                    <Form.Row>
                                        <Col md="12">
                                            <div className="descriptor-item">
                                                <h6 className="float-left">Type</h6>
                                                <h6 className="ginfo float-right">
                                                    {controller.type || controller.controller_type || DEFAULT_VALUES.CONTROLLER_TYPE}
                                                </h6>
                                            </div>
                                            <div className="descriptor-item">
                                                <h6 className="float-left">Auxiliary Hub Name</h6>
                                                <h6 className="ginfo float-right">
                                                    {controller.ldsuName || DEFAULT_VALUES.CONTROLLER_NAME}
                                                </h6>
                                            </div>
                                            <div className="descriptor-item">
                                                <h6 className="float-left">Auxiliary Hub UUID</h6>
                                                <h6 className="ginfo float-right">
                                                    {controller.UID || DEFAULT_VALUES.CONTROLLER_ID}
                                                </h6>
                                            </div>
                                            <div className="descriptor-item">
                                                <h6 className="float-left">Network Name</h6>
                                                <h6 className="ginfo float-right">
                                                    {networkName}
                                                </h6>
                                            </div>
                                            <div className="descriptor-item">
                                                <h6 className="float-left">
                                                    {zoneMembership.length > 0
                                                        ? "Zone membership"
                                                        : "Room membership"}
                                                </h6>
                                                <h6 className="ginfo float-right">
                                                    {zoneOrRoomMembershipValue}
                                                </h6>
                                            </div>
                                            <div className="descriptor-item">
                                                <h6 className="float-left">Groups membership</h6>
                                                <h6 className="ginfo float-right">
                                                    {groupMembership.length > 0
                                                        ? groupMembership.join(", ")
                                                        : DEFAULT_VALUES.ROOM_MEMBERSHIP}
                                                </h6>
                                            </div>
                                        </Col>
                                    </Form.Row>
                                </div>
                            </div>
                        </Col>

                        <Col lg={6} xs={12}>
                            {/* Download Section */}
                            <div className="form-box download-controller-data">
                                <h5>Download actuator data</h5>
                                <DownloadDataUI
                                    gatewayId={id}
                                    ldsuId={controller?.UID}
                                    said={controller?.said || controllerSaid}
                                    downloadType="downloadController"
                                    emailLabel="Recieve Link"
                                />
                            </div>
                        </Col>
                    </Row>
                </Container>
            </div>
        </ContentWrapper>
    );
};

export default ControllerDetails; 